import React from "react";
import moment from "moment";
import GatsbyConfig from "../../../gatsby-config.js";
import {
  getEl,
  formatPrice,
  getInLocal,
  setInLocal,
  trackEvent,
  getParamFromQueryString,
  getRoomPrice,
  getValidCoupon,
} from "../../utils/app-helpers";
import { getKeyPairsInForm } from "../../utils/forms";
import CouponField from "../partials/coupon-field";
import CouponReminder from "../partials/coupon-reminder";
import SiteFooter from "../partials/site-footer";
import SiteHeader from "../partials/site-header";
import TripSummary from "../partials/trip-summary";
import TravelerSummary from "../partials/traveler-summary";

const { siteMetadata: config } = GatsbyConfig;

export default class PayPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { coupon: "", isProcessing: false };
  }

  componentDidMount() {
    const { data } = this || {};
    const { trip } = this.props;

    if (trip) {
      const tripData = config.trips[trip];

      if (tripData.soldOut) return;
      trackEvent("AddPaymentInfo", {
        content_name: `${tripData.name} ${tripData.year}`,
      });
    }

    this.stripeWatch = setInterval(
      (() => {
        const cardEl = getEl("#card-element");
        const cardErrorsEl = getEl("#card-errors");

        if (cardEl && window.Stripe) {
          this.stripe = window.Stripe(process.env.GATSBY_STRIPE_PUBLIC_KEY);
          this.elements = this.stripe.elements(cardEl);
          this.card = this.elements.create("card", {
            style: config.stripe.style,
          });
          this.card.mount("#card-element");
          this.card.addEventListener("change", ({ error }) => {
            if (error) {
              cardErrorsEl.textContent = error.message;
              cardErrorsEl.classList.remove("hide");
            } else {
              cardErrorsEl.classList.add("hide");
              cardErrorsEl.textContent = "";
            }
          });
          clearInterval(this.stripeWatch);
        }
      }).bind(this),
      200
    );
    setTimeout(window.scrollTo, 150, 0, 0);
  }

  submitData(e) {
    e.preventDefault();
    const { stripe, card } = this;
    const defaultErr = "Unable to submit payment.";
    const formEl = getEl("form");
    const keyPairs = getKeyPairsInForm(formEl);
    const cardErrorsEl = getEl("#card-errors");
    const data = getInLocal("lsg.book-data", true);

    for (const entry of keyPairs) {
      data[entry[0]] = entry[1];
    }
    setInLocal("lsg.book-data", JSON.stringify(data));
    this.data = data;

    if (!stripe) return (cardErrorsEl.textContent = defaultErr);
    cardErrorsEl.classList.add("hide");
    cardErrorsEl.textContent = "";
    stripe.createToken(card).then(
      (({ token, error }) => {
        if (error) {
          cardErrorsEl.textContent = error.message;
          cardErrorsEl.classList.remove("hide");
        } else {
          this.stripeTokenHandler(token);
        }
      }).bind(this)
    );
  }

  stripeTokenHandler(token) {
    const { data, room } = this;
    const tripData = config.trips[data.trip] || {};
    const useMaxSubMonthsCutoff = moment(
      tripData.useMaxSubMonthsCutoff,
      "MM-DD-YYYY"
    );
    const availabilityCutoff = moment(
      tripData.availabilityCutoff,
      "MM-DD-YYYY"
    );
    const today = moment(new Date());
    const maxMonths = tripData.maxSubMonths;
    let xMonths;

    if (today.valueOf() > useMaxSubMonthsCutoff.valueOf()) {
      xMonths = Math.floor(availabilityCutoff.diff(today, "months", true)) + 1;
      if (xMonths > maxMonths) xMonths = maxMonths;
    } else {
      xMonths = maxMonths;
    }
    const useHawaiiPricing =
      tripData.useHawaiiPricing &&
      tripData.rooms[room] &&
      tripData.rooms[room].prices.hawaii &&
      isHawaiiTime();
    const subscriptionFee = tripData.subscriptionFee * 100;
    const perPersonFees = tripData.taxesAndFees * 100;
    const priceFull = getRoomPrice(tripData, room, data.promo) * 100;
    const priceMonthly = Math.round((priceFull + subscriptionFee) / xMonths);
    const roomRate = priceFull - perPersonFees;
    const form = getEl("form");

    const couponCode = data.coupon || "";
    const matchedCoupon = getValidCoupon(couponCode, tripData.coupons);
    const couponValue = matchedCoupon ? matchedCoupon.value : "$0";
    const couponValueInt = parseInt(couponValue.replace(/\D/g, ""), 10);
    let discountFull = 0;
    let discountMonthly = 0;
    if (couponValueInt && couponValue.match("%")) {
      discountFull = (priceFull * couponValueInt) / 100;
      discountMonthly = (priceMonthly * couponValueInt) / 100;
    } else if (couponValueInt && couponValue.match("$")) {
      discountFull = couponValueInt * 100;
      discountMonthly = discountFull / xMonths;
    }

    const amount = data.pay === "full" ? priceFull : priceMonthly;
    const discount = data.pay === "full" ? discountFull : discountMonthly;
    const { protocol, hostname, port } = window.location;

    const params = {
      returnPathRoot: `${protocol}//${hostname}${port ? ":" + port : ""}/${
        data.trip
      }`,
      email: data.email,
      amount: amount,
      discount: discount,
      source: token.id,
      room: data.room,
      trip: data.trip,
      pay: data.pay,
      xMonths: xMonths,
      couponCode: matchedCoupon ? couponCode : "",
      couponValue: couponValue,
      emaildata: JSON.stringify({
        firstName: data.firstName,
        firstNameSpouse: data.firstNameSpouse,
        lastName: data.lastName,
        lastNameSpouse: data.lastNameSpouse,
        tripDates: tripData.dates,
        tripStartTime: tripData.startTime,
        tripName: tripData.name,
        tripYear: tripData.year,
        tripRegion: tripData.region,
        tripCountry: tripData.country,
        tripVenue: tripData.venue,
        checkInDay: tripData.checkInDay,
        checkInDate: tripData.checkInDate,
        checkInTime: tripData.checkInTime,
        checkOutDay: tripData.checkOutDay,
        checkOutDate: tripData.checkOutDate,
        checkOutTime: tripData.checkOutTime,
        arrivePriorTo: tripData.arrivePriorTo,
        roomName: room.name,
        paymentOption: data.pay,
        checkOutTime: tripData.checkOutTime,
        departureAirport: tripData.departureAirport,
        departureAirCode: tripData.departureAirCode,
        xMonths: xMonths,
        amount: formatPrice(amount),
        amountPP: formatPrice(amount / 2),
        roomRate: formatPrice(roomRate),
        roomRatePP: formatPrice(roomRate / 2),
        taxesAndFees: formatPrice(perPersonFees),
        taxesAndFeesPP: formatPrice(perPersonFees / 2),
        couponCode: matchedCoupon ? couponCode : "",
        couponValue: matchedCoupon ? couponValue : "",
        upgradedFrom: data.upgraded,
        referralSource: data.referralSource,
        referralSourceExplanation: data.referralSourceExplanation || "",
        usedHawaiiPricing: useHawaiiPricing,
        twoDaysBeforePaymentEndDate: today
          .add("months", xMonths - 1)
          .subtract("days", 2)
          .format("MMMM D, YYYY"),
      }),
      metadata: JSON.stringify({
        "Trip Name": `${tripData.name} ${tripData.year}`,
        "Room Name": room.name,
        "Contact Address": `${data.address}, ${data.city}, ${data.region}, ${data.zip}, ${data.country}`,
        "Contact Email": data.email,
        "Contact Phone": data.phone,
        "Coupon Code": matchedCoupon ? couponCode : "",
        "Coupon Value": matchedCoupon ? couponValue : "",
        "Primary Name": `${data.firstName} ${data.lastName}`,
        "Primary Birthday": `${data.birthMonth} ${data.birthDay}, ${data.birthYear}`,
        "Spouse Name": `${data.firstNameSpouse} ${data.lastNameSpouse}`,
        "Spouse Birthday": `${data.birthMonthSpouse} ${data.birthDaySpouse}, ${data.birthYearSpouse}`,
        Referral:
          data.referralSource +
          (data.referralSourceExplanation
            ? ` - ${data.referralSourceExplanation}`
            : ""),
        "Upgraded From": data.upgraded,
      }),
    };
    let keyPairs = [];
    for (let key in params) {
      keyPairs.push(`${key}=${encodeURIComponent(params[key])}`);
    }

    this.data.amount = amount;
    this.data.discount = discount;
    this.data.xMonths = xMonths;
    setInLocal("lsg.book-data", JSON.stringify(this.data));

    this.setState({ isProcessing: true });
    form.setAttribute(
      "action",
      `${process.env.GATSBY_ENDPOINT_SUBSCRIBE}?${keyPairs.join("&")}`
    );
    form.submit();
  }

  onChangeCoupon = (coupon) => {
    this.data.coupon = coupon;
    setInLocal("lsg.book-data", JSON.stringify(this.data));
    this.setState({ coupon });
  };

  render() {
    if (typeof window === "undefined") return <div />;
    const { trip } = this.props;
    const { isProcessing } = this.state;
    const tripData = config.trips[trip];
    const data = getInLocal("lsg.book-data", true);
    const room = tripData.rooms[data.room];
    const coupon = this.state.coupon || data.coupon || "";
    const validCoupon = tripData.useCoupons
      ? getValidCoupon(coupon, tripData.coupons)
      : null;
    const promo = getParamFromQueryString("promo", window.location.href);

    if (!room || !room.key) {
      window.location.href = `/${trip}/prices`;
      return <main />;
    }
    this.data = data;
    this.room = room;
    return (
      <div className={`page-pay trip-${trip}`}>
        <SiteHeader
          trip={trip}
          step="5"
          lock
          lockLogo={promo === "onlocation"}
        />
        <CouponReminder trip={trip} />
        {tripData.soldOut ? (
          <main>
            <p className="soldout-notice">Trip Sold Out</p>
          </main>
        ) : (
          <main className="form">
            <form
              id="payment-form"
              method="POST"
              onSubmit={(e) => e.preventDefault()}
            >
              {isProcessing ? (
                <div className="loader-mask fixed">
                  <p>Processing Payment</p>
                  <div className="loader" />
                </div>
              ) : (
                ""
              )}
              <div className="wrap content">
                <h2>Your Love Song Getaway</h2>
                <TripSummary room={room} trip={trip} />
                {tripData.useCoupons &&
                tripData.coupons &&
                tripData.coupons.length ? (
                  <CouponField
                    trip={trip}
                    coupon={coupon}
                    onChange={this.onChangeCoupon}
                  />
                ) : (
                  ""
                )}
                <h2>Traveler Info</h2>
                <TravelerSummary data={this.data} />
                <h2>Credit Card</h2>
                <fieldset>
                  <p className="field card-wrap">
                    <span id="card-element" />
                  </p>
                  <p className="error hide" id="card-errors" />
                </fieldset>
                <p className="button-bar full">
                  <button
                    className="btn-primary"
                    onClick={(e) => this.submitData(e)}
                  >
                    Submit Payment
                  </button>
                </p>
              </div>
            </form>
          </main>
        )}
        <SiteFooter trip={trip} hideTrips hideNewsletter />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/pay.js